import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL } from "@/mocks/categories";
import { articles, type ArticleContent } from "@/mocks/articles";
import JsonLd from "@/components/seo/JsonLd";
import { SITE_URL, SITE_LOGO_URL } from "@/components/seo/siteUrl";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const meta = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const content = useMemo(() => {
    if (!slug) return null;
    const value = t(`articles.${slug}`, { returnObjects: true });
    if (typeof value === "string") return null;
    return value as unknown as ArticleContent;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, i18n.language]);

  const related = useMemo(() => {
    const others = articles.filter((a) => a.slug !== slug).slice(0, 3);
    return others;
  }, [slug]);

  if (!meta || !content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex flex-col items-center justify-center px-4 pt-40 pb-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">
            {t("article.notFound")}
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            {t("article.backHome")}
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: content.h1,
    description: content.metaDescription,
    image: meta.image,
    author: { "@type": "Organization", name: content.author },
    publisher: {
      "@type": "Organization",
      name: "USFans Spreadsheet",
      logo: { "@type": "ImageObject", url: SITE_LOGO_URL },
    },
    datePublished: "2026-01-15",
    dateModified: "2026-08-20",
    mainEntityOfPage: `${SITE_URL}/guide/${slug}`,
    inLanguage: i18n.language,
  };

  const faqSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: content.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={faqSchema} />
      <Navbar />
      <main className="pt-24">
        <article className="mx-auto max-w-3xl px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">
              {t("article.backHome")}
            </Link>
            <span className="text-foreground-300">/</span>
            <span>{content.categoryLabel}</span>
          </nav>

          {/* Header */}
          <header className="mt-6">
            <span className="inline-flex items-center rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
              {content.categoryLabel}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {content.h1}
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-foreground-600">
              {content.subtitle}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </span>
                {content.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {content.readTime}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </span>
                2026
              </span>
            </div>
          </header>

          {/* Hero image */}
          <div className="relative mt-8 h-64 w-full overflow-hidden rounded-xl md:h-80">
            <img
              src={meta.image}
              alt={content.h1}
              title={content.h1}
              className="h-full w-full object-cover object-top"
            />
          </div>

          {/* Quick answer */}
          <div className="mt-8 rounded-lg border-l-4 border-primary-500 bg-background-100 p-5">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 flex shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-flashlight-line text-sm"></i>
              </span>
              <p className="text-base leading-relaxed text-foreground-700">
                {content.quickAnswer}
              </p>
            </div>
          </div>

          {/* Intro */}
          <div className="mt-8 space-y-4">
            <p className="text-base leading-relaxed text-foreground-700">
              {content.intro1}
            </p>
            <p className="text-base leading-relaxed text-foreground-700">
              {content.intro2}
            </p>
          </div>

          {/* Body sections */}
          <div className="mt-10 space-y-10">
            {content.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-heading text-2xl font-bold text-foreground-950">
                  {section.h}
                </h2>
                {section.p1 && (
                  <p className="mt-4 text-base leading-relaxed text-foreground-700">
                    {section.p1}
                  </p>
                )}
                {section.p2 && (
                  <p className="mt-4 text-base leading-relaxed text-foreground-700">
                    {section.p2}
                  </p>
                )}
                {section.items && (
                  <ul className="mt-4 space-y-2.5">
                    {section.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2.5 text-base leading-relaxed text-foreground-700"
                      >
                        <span className="mt-1.5 w-5 h-5 flex shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                          <i className="ri-check-line text-sm"></i>
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          {/* Key takeaways */}
          <div className="mt-12 rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              {content.takeawaysTitle}
            </h2>
            <ul className="mt-4 space-y-2.5">
              {content.takeaways.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2.5 text-base leading-relaxed text-foreground-700"
                >
                  <span className="mt-1.5 w-5 h-5 flex shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className="ri-star-line text-sm"></i>
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* FAQ */}
          <section className="mt-12">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              {t("article.faqTitle")}
            </h2>
            <div className="mt-6 space-y-3">
              {content.faq.map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div
                    key={item.q}
                    className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                        {item.q}
                      </span>
                      <span className="w-6 h-6 flex shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600">
                        <i
                          className={`ri-arrow-down-s-line transition-transform ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        ></i>
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5">
                        <p className="text-sm leading-relaxed text-foreground-600">
                          {item.a}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Related + CTA */}
          <section className="mt-12 rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              {t("article.relatedTitle")}
            </h2>
            <ul className="mt-4 space-y-2.5">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/guide/${r.slug}`}
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                    {t(`articles.${r.slug}.h1`)}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-6 border-t border-background-200 pt-6">
              <h3 className="font-heading text-base font-semibold text-foreground-950">
                {t("article.ctaTitle")}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {t("article.ctaText")}
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                >
                  {t("article.backHome")}
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </span>
                </Link>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 rounded-full border border-primary-500 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 whitespace-nowrap cursor-pointer"
                >
                  {t("cta.visitStore")}
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line"></i>
                  </span>
                </a>
              </div>
            </div>
          </section>
        </article>
      </main>
      <div className="mt-16">
        <Footer />
      </div>
    </div>
  );
}